import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, CircularProgress, Typography,
} from '@mui/material';
import { toBrazilianDate, formatCpfCnpj } from '../../../../helpers/utils/utils';
import { formatBRL } from '../../UI';

/**
 * Dialog "Resumo" da grid de Antecedentes (AW2-1033 Etapa 5) — port
 * simplificado de `AcompanhamentoProcesso` real (`Acompanhamento.jsx`) para
 * MUI v5, restrito aos campos que o plano de arquitetura define como
 * relevantes aqui (os que não duplicam o card "Cálculo do Processo" já
 * exibido para o processo principal): Valor da Causa, Valor Total Custas,
 * Cliente/Devedor (código+nome+CPF/CNPJ), Data Abertura, Data Expiração —
 * todos somente-leitura, sem seção de Endossante (replicando
 * `disableEditarEndossante={true}` do uso real).
 *
 * Componente "burro" — não busca dado sozinho. `AbaAcompanhamento.jsx` faz o
 * fetch (`buscarAcompanhamentoDoProcessoService`) no clique do botão
 * "Resumo" e repassa `carregando`/`erro`/`dados` já resolvidos, mesmo padrão
 * já usado por `DialogSimularQuitacao.jsx` no mesmo arquivo.
 */
function Campo({ label, valor }) {
  return (
    <Typography variant="body2" sx={{ mb: 0.5 }}>
      <b>
        {label}
        :
        {' '}
      </b>
      {valor}
    </Typography>
  );
}

Campo.propTypes = {
  label: PropTypes.string.isRequired,
  valor: PropTypes.node,
};

Campo.defaultProps = {
  valor: '',
};

function getClienteOuDevedor(codigo, nome, cnpjCpf) {
  if (codigo == null || !nome) {
    return '—';
  }
  const identificacao = `${codigo} - ${nome}`;
  const documento = cnpjCpf ? formatCpfCnpj(cnpjCpf) : null;
  return documento ? `${identificacao} (${documento})` : identificacao;
}

export default function DialogResumoAntecedente({
  open, numeroAntecedente, carregando, erro, dados, onClose,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Resumo
        {numeroAntecedente ? ` — Processo ${numeroAntecedente}` : ''}
      </DialogTitle>
      <DialogContent>
        {carregando && (
          <Box sx={{
            display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200,
          }}
          >
            <CircularProgress />
          </Box>
        )}
        {!carregando && erro && (
          <Typography variant="body2" color="error">{erro}</Typography>
        )}
        {!carregando && !erro && dados && (
          <Box>
            <Campo label="Valor da Causa" valor={dados.valorCausa == null ? '—' : formatBRL(dados.valorCausa)} />
            <Campo label="Valor Total Custas" valor={dados.valorTotalCustas == null ? '—' : formatBRL(dados.valorTotalCustas)} />
            <Campo label="Cliente" valor={getClienteOuDevedor(dados.codigoCliente, dados.nomeCliente, dados.cnpjCpfCliente)} />
            <Campo label="Devedor" valor={getClienteOuDevedor(dados.codigoDevedor, dados.nomeDevedor, dados.cnpjCpfDevedor)} />
            <Campo label="Data da Abertura" valor={dados.dataAbertura ? toBrazilianDate(dados.dataAbertura) : '—'} />
            <Campo label="Data da Expiração" valor={dados.dataExpiracao ? toBrazilianDate(dados.dataExpiracao) : '—'} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

DialogResumoAntecedente.propTypes = {
  open: PropTypes.bool,
  numeroAntecedente: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  carregando: PropTypes.bool,
  erro: PropTypes.string,
  dados: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

DialogResumoAntecedente.defaultProps = {
  open: false,
  numeroAntecedente: null,
  carregando: false,
  erro: '',
  dados: null,
};
